/**
 * Canonical tool-key map (mirror of profiteia-backend/api/tools/tool_keys.py).
 *
 * Canonical = the frontend route slug. Keep in sync with the backend file;
 * the backend is the authority (see Step -1 §B of the cockpit plan), but most
 * UI surfaces are happy with this local mirror.
 */

import {
  mdiBookOpenVariant,
  mdiChartBellCurveCumulative,
  mdiChartLineVariant,
  mdiClipboardCheckOutline,
  mdiFlash,
  mdiFlaskOutline,
  mdiHandshakeOutline,
  mdiLightningBolt,
  mdiMonitorEye,
  mdiPulse,
  mdiScaleBalance,
  mdiShieldHalfFull,
  mdiSwapHorizontal,
  mdiTransitConnectionVariant,
  mdiTransmissionTower,
  mdiVectorPolyline,
  mdiWaterAlertOutline,
} from '@mdi/js';

// Labels are tuned to fit a single sidebar row (~22 chars at 14px). Longer
// product names are shortened to
// the core noun so the sidebar stays scannable; the canonical key + the
// full page titles (`page_titles.*` in i18n) keep the verbose form.
//
// `labelKey` is the i18n key for the display label (`tool_labels.<key>`);
// `label` stays as the (NL) fallback for surfaces/tests without i18n. Render
// via `toolLabel(tool, t, te)` — never `tool.label` directly.
// Visible prediction tools use "prediction" / "voorspelling"; older
// "predictor" aliases stay route-compatible but should not be user-facing.
//
// `featureFlag` (optional) mirrors the tool route's `meta.requiresFeatureFlag`
// (see src/router/routes/tools.js). When set, the tool is hidden from the
// sidebar and the /tools collection page while the flag is disabled — so we
// never show a link whose route guard would just redirect the user home.
// Keep it in sync with the route meta.
//
// `stage` (optional): 'alpha' | 'beta'. Renders a stage chip (via
// ToolStageBadge) on the tool's own page, its /tools grid card, and its
// benchmark-wizard header where applicable. Omit once a tool is generally
// available.
export const CANONICAL_TOOLS = [
  // Forecasts
  {
    key: 'day-ahead-prediction',
    labelKey: 'tool_labels.day-ahead-prediction',
    label: 'Day-ahead',
    category: 'forecasts',
    deepLink: '/tools/day-ahead-forecast',
    icon: mdiChartLineVariant,
    aliases: ['day-ahead-prediction', 'day-ahead-predictor', 'day-ahead-forecast'],
  },
  {
    key: 'control-state-prediction',
    labelKey: 'tool_labels.control-state-prediction',
    label: 'Regeltoestandvoorspelling',
    category: 'forecasts',
    deepLink: '/tools/control-state',
    icon: mdiPulse,
    aliases: ['control-state', 'control-state-prediction', 'control-state-predictor', 'rt2'],
  },
  {
    key: 'capacity-fee-forecaster',
    labelKey: 'tool_labels.capacity-fee-forecaster',
    label: 'Capaciteitsvergoeding',
    category: 'forecasts',
    deepLink: '/tools/capacity-fee-forecaster',
    icon: mdiTransitConnectionVariant,
    aliases: ['capacity-fee-forecaster'],
    featureFlag: 'capacity-fee-forecaster',
  },
  {
    key: 'spike-probability',
    labelKey: 'tool_labels.spike-probability',
    label: 'Spike probability',
    category: 'forecasts',
    deepLink: '/tools/spike-probability',
    icon: mdiChartBellCurveCumulative,
    aliases: ['spike-probability'],
    featureFlag: 'spike-probability',
  },
  // Benchmarks
  {
    key: 'bidding-strategy',
    labelKey: 'tool_labels.bidding-strategy',
    label: 'Biedstrategie-backtester',
    category: 'benchmarks',
    deepLink: '/tools/bidding-strategy',
    icon: mdiClipboardCheckOutline,
    aliases: ['bidding-strategy', 'bidding_strategy', 'backtester'],
  },
  {
    key: 'forward-benchmark',
    labelKey: 'tool_labels.forward-benchmark',
    label: 'Opbrengstprognose',
    category: 'benchmarks',
    deepLink: '/tools/forward-benchmark',
    icon: mdiScaleBalance,
    aliases: ['forward-benchmark', 'forward_benchmark'],
    featureFlag: 'forward-benchmark',
  },
  {
    key: 'hedging-benchmark',
    labelKey: 'tool_labels.hedging-benchmark',
    label: 'Hedging Benchmark',
    category: 'benchmarks',
    deepLink: '/tools/hedging-benchmark',
    icon: mdiShieldHalfFull,
    aliases: ['hedging-benchmark', 'hedging_benchmark'],
    featureFlag: 'hedging-benchmark',
    stage: 'alpha',
  },
  {
    key: 'imbalance-benchmark',
    labelKey: 'tool_labels.imbalance-benchmark',
    label: 'Onbalansbenchmark',
    category: 'benchmarks',
    deepLink: '/tools/imbalance-benchmark',
    icon: mdiSwapHorizontal,
    aliases: ['imbalance-benchmark', 'imbalance_benchmark'],
  },
  // Simulators
  {
    key: 'shadow-trading',
    labelKey: 'tool_labels.shadow-trading',
    label: 'Shadow Trading',
    category: 'simulators',
    deepLink: '/tools/shadow-trading',
    icon: mdiFlaskOutline,
    aliases: ['shadow-trading', 'shadow_trading'],
    featureFlag: 'shadow-trading',
    stage: 'beta',
  },
  {
    key: 'power-market-simulator',
    labelKey: 'tool_labels.power-market-simulator',
    label: 'Marktsimulator',
    category: 'simulators',
    deepLink: '/tools/power-market-simulator',
    icon: mdiLightningBolt,
    aliases: ['power-market-simulator', 'power_market_simulator'],
    featureFlag: 'power-market-simulator',
  },
  // Reports moved to the Personalisation cauldron ("Mijn omgeving") — a report
  // is a user-owned artifact, not a live market tool. See router
  // myEnvironment.js (/my-environment/reports).
  // Monitors (live, read-only market observation — present tense)
  {
    key: 'orderbook',
    labelKey: 'tool_labels.orderbook',
    label: 'Orderbook',
    category: 'monitors',
    deepLink: '/tools/orderbook',
    icon: mdiBookOpenVariant,
    aliases: ['orderbook'],
  },
  {
    key: 'generation-map',
    labelKey: 'tool_labels.generation-map',
    label: 'Opwekkaart',
    category: 'monitors',
    deepLink: '/tools/generation-map',
    icon: mdiFlash,
    aliases: ['generation-map', 'generation_map'],
  },
  {
    // The PyPSA-Eur transmission grid with live per-country overlays. Rides on
    // the generation-map entitlement: its live layers already require it.
    key: 'grid-map',
    labelKey: 'tool_labels.grid-map',
    label: 'Netkaart',
    category: 'monitors',
    deepLink: '/tools/grid-map',
    icon: mdiVectorPolyline,
    aliases: ['grid-map', 'grid_map', 'grid-topology', 'grid-status'],
    stage: 'beta',
  },
  {
    key: 'grid-state',
    labelKey: 'tool_labels.grid-state',
    label: 'Netstatus',
    category: 'forecasts',
    deepLink: '/tools/grid-state',
    icon: mdiTransmissionTower,
    aliases: ['grid-state', 'grid_state', 'grid-state-forecast'],
    stage: 'alpha',
  },
  {
    key: 'water-risk',
    labelKey: 'tool_labels.water-risk',
    label: 'Waterrisico',
    category: 'monitors',
    deepLink: '/tools/water-risk',
    icon: mdiWaterAlertOutline,
    aliases: ['water-risk', 'water_risk', 'water-level-risk'],
    featureFlag: 'water-risk',
  },
  {
    key: 'distribution-interruptions',
    labelKey: 'tool_labels.distribution-interruptions',
    label: 'Netstoringen',
    category: 'monitors',
    deepLink: '/tools/distribution-interruptions',
    icon: mdiLightningBolt,
    aliases: ['distribution-interruptions', 'distribution_interruptions', 'netstoringen'],
    featureFlag: 'distribution-interruptions',
    stage: 'alpha',
  },
];

const _aliasIndex = (() => {
  const map = new Map();
  for (const tool of CANONICAL_TOOLS) {
    for (const alias of tool.aliases) map.set(alias, tool);
  }
  return map;
})();

export function canonicalize(name) {
  return _aliasIndex.get(name) || null;
}

export function canonicalKey(name) {
  const tool = _aliasIndex.get(name);
  return tool ? tool.key : name;
}

export function getToolByKey(key) {
  return canonicalize(key);
}

/**
 * i18n-aware display label for a tool: `t(tool.labelKey)` when the key exists
 * in the active locale, otherwise the hardcoded `label` fallback. Pass the
 * component's `t`/`te` from `useI18n()` (`te` may be missing in test mocks).
 */
export function toolLabel(tool, t, te) {
  return tool?.labelKey && typeof te === 'function' && te(tool.labelKey)
    ? t(tool.labelKey)
    : tool?.label || tool?.key || '';
}

export function toolsByCategory() {
  const grouped = {};
  for (const tool of CANONICAL_TOOLS) {
    grouped[tool.category] = grouped[tool.category] || [];
    grouped[tool.category].push(tool);
  }
  return grouped;
}

/**
 * Filter a list of tools down to the ones that should be VISIBLE given the
 * current feature-flag state. A tool with no `featureFlag` is always visible;
 * a flagged tool is visible only when its flag resolves enabled.
 *
 * This keeps the registry pure (no auth-store/reactivity coupling): callers
 * pass in a resolver — typically `useFeatureFlags().isFeatureEnabled` — so the
 * sidebar and the tools-collection page never surface a tool whose route guard
 * (`meta.requiresFeatureFlag`) would just bounce the user back home.
 *
 * @param {Array<object>} tools
 * @param {(featureKey: string) => boolean} isFeatureEnabled
 * @returns {Array<object>}
 */
export function filterToolsByFeatureFlag(tools, isFeatureEnabled) {
  if (typeof isFeatureEnabled !== 'function') return tools || [];
  return (tools || []).filter((tool) => !tool.featureFlag || isFeatureEnabled(tool.featureFlag));
}

export const TOOL_CATEGORIES = ['forecasts', 'benchmarks', 'simulators', 'monitors'];

export const CATEGORY_META = {
  forecasts: {
    key: 'forecasts',
    titleKey: 'tools_collection.categories.forecasts',
    descriptionKey: 'tools_collection.category_descriptions.forecasts',
    eyebrowKey: 'tools_collection.eyebrows.forecasts',
    icon: mdiChartLineVariant,
  },
  benchmarks: {
    key: 'benchmarks',
    titleKey: 'tools_collection.categories.benchmarks',
    descriptionKey: 'tools_collection.category_descriptions.benchmarks',
    eyebrowKey: 'tools_collection.eyebrows.benchmarks',
    icon: mdiHandshakeOutline,
  },
  simulators: {
    key: 'simulators',
    titleKey: 'tools_collection.categories.simulators',
    descriptionKey: 'tools_collection.category_descriptions.simulators',
    eyebrowKey: 'tools_collection.eyebrows.simulators',
    icon: mdiFlash,
  },
  monitors: {
    key: 'monitors',
    titleKey: 'tools_collection.categories.monitors',
    descriptionKey: 'tools_collection.category_descriptions.monitors',
    eyebrowKey: 'tools_collection.eyebrows.monitors',
    icon: mdiMonitorEye,
  },
};

export function getCategoryMeta(category) {
  return CATEGORY_META[category] || null;
}

export default {
  CANONICAL_TOOLS,
  TOOL_CATEGORIES,
  CATEGORY_META,
  canonicalize,
  canonicalKey,
  getToolByKey,
  toolLabel,
  toolsByCategory,
  filterToolsByFeatureFlag,
  getCategoryMeta,
};
